'use client';

import Link from 'next/link';
import type { ReactNode } from 'react';
import { useSiteSettings } from '@/components/providers';
import { ContactModuleModal } from './ContactModuleModal';

type ContactCtaProps = {
    /** Names the dialog for screen readers, and labels the trigger. */
    label: string;
    /** Where the button goes while the contact module has no form set. */
    fallbackHref: string;
    className?: string;
    children?: ReactNode;
};

/**
 * A `linkType: 'contact'` item, rendered. The forms are already in the client
 * settings the whole app is wrapped in, so this needs no props from the server
 * and can therefore sit behind `BaseLink` — wherever a nav item becomes a link,
 * including every CTA button.
 *
 * The fallback is the same bargain the floating CTA strikes: until an editor
 * gives the contact forms a heading there is nothing to open, so the item
 * leads to its page instead rather than being a button that does nothing.
 */
export function ContactCta({ label, fallbackHref, className, children }: ContactCtaProps) {
    const content = useSiteSettings().contactModule;

    if (!content?.title) {
        return (
            <Link href={fallbackHref} className={className}>
                {children ?? label}
            </Link>
        );
    }

    return (
        <ContactModuleModal content={content} label={label} className={className}>
            {children ?? label}
        </ContactModuleModal>
    );
}
